// The Sourcing and Files steps (Costs is CostsTab.tsx): the project's parts in the usual table (DataTable: views, columns,
// Copy / CSV) with what each has and lacks (the same rules as readiness() in shared/projects.ts), opening the item panel
// or the spec in place. A part's Category can be set
// here, one at a time or for every material and sub-assembly ticked (item.set_part_type); a packaging part's on its row
// (pack_type). Packaging --- a part's files come from partNeeds() (a packaging part's from its Category and printing),
// and count once they're in the released revision: what vendors get. Supply: who usually supplies a part (its BOM),
// and each run of the project that says otherwise for its batch (migration 057), changed on that run's Plan tab.
import { useMemo, useState } from "react";
import { bomOf, byCm, type Supply } from "../../../../shared/boms.ts";
import { categoryLabel, categoryOf, categoryOptions, typeLabel, typeOf } from "../../../../shared/part-type.ts";
import { filesOf, type PackType, type PartType, type ProjectItem, partList, projectPath, type ScopePart, sourcingOf } from "../../../../shared/projects.ts";
import { RUN_SUPPLIES, runPath, supplyWords } from "../../../../shared/runs.ts";
import { days, money, qty } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { Link, setSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { PageSection } from "../../ui/PageParts.tsx";
import { PaneLink } from "../../ui/SidePane.tsx";
import { NeedChips, packCategoryPatch } from "../PackagingSection.tsx";
import { BuildSupply } from "./BuildSupply.tsx";
import { StepGaps, type TabProps } from "./Workspace.tsx";

const Missing = ({ children }: { children: string }) => <span className="text-[12px] font-medium text-warn">{children}</span>;
const _pct = (m: number | null) => (m == null ? "—" : `${Math.round(m * 100)}%`);
/** A part's Type and Category, a sell item used as a part (a Shopify joystick) taken as a material: its Category decides
 * its spec's files too. */
const asPart = (item: ProjectItem) => (item.kind === "sell" ? { ...item, kind: "material" as const } : item);

function Filter({ value, onChange, counts }: { value: "needs" | "all"; onChange: (v: "needs" | "all") => void; counts: [number, number] }) {
  return (
    <div className="flex rounded-md border border-line p-0.5">
      {(
        [
          ["needs", `Needs work ${counts[0]}`],
          ["all", `All ${counts[1]}`],
        ] as const
      ).map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cx("h-6 rounded px-2 text-[12px]", value === v ? "bg-selected font-medium" : "text-muted hover:bg-hover")}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const open = (id: string) => setSearch({ open: id });
/** A project nothing is in yet (JellyCaps before it has a BOM). */
export const NO_PARTS = "No parts yet: a part is in a project when one of its BOMs uses it.";

/** The same with where to go: parts in no project (where a project's old parts are now), and Products to link one. */
function NoParts({ project, scope }: Pick<TabProps, "project" | "scope">) {
  return (
    <>
      {NO_PARTS}{" "}
      <Link to="/parts?project=none" className="text-accent-text hover:underline">
        Parts in no project
      </Link>
      {!scope.products.length && (
        <>
          {" · "}
          <Link to={projectPath(project, "products")} className="text-accent-text hover:underline">
            Link a product
          </Link>
        </>
      )}
    </>
  );
}

// Sourcing ----------------------------------------------------------------------------------------------

type SourcingRow = { id: string; p: ScopePart; s: ReturnType<typeof sourcingOf>; terms: boolean; cmOnly: boolean; gaps: string[] };

export function SourcingTab({ project, px, scope, r, here }: TabProps) {
  const ix = px.ix;
  // Every part to start (Mitch: "I don't understand the purpose of the 'Bought parts' section"): what each goes into and
  // who supplies it; Needs work shows only the ones still missing a vendor, price, MOQ or lead time.
  const [filter, setFilter] = useState<"needs" | "all">("all");
  const rows = useMemo<SourcingRow[]>(() => {
    const out = partList(scope)
      .filter((p) => {
        const item = ix.itemById.get(p.item.id);
        return p.item.kind !== "subassembly" && !(item?.kind === "sell" && bomOf(ix, item));
      })
      .map((p) => {
        const s = sourcingOf(px, p.item.id);
        // Only the CM buys or makes it: the CM's to source, nothing asked of HHL (a price HHL knows still costs it).
        const cmOnly = p.uses.length > 0 && [...p.supplies].every(byCm);
        const terms = !cmOnly && p.item.kind !== "service";
        const gaps = cmOnly
          ? []
          : [
              !s.link && "vendor",
              s.link && s.price == null && "price",
              s.link && terms && s.moq == null && "moq",
              s.link && terms && s.lead == null && "lead",
            ].filter((g): g is string => !!g);
        return { id: p.item.id, p, s, terms, cmOnly, gaps };
      });
    return out.sort((a, b) => Number(b.gaps.length > 0) - Number(a.gaps.length > 0));
  }, [px, scope, ix]);
  const shown = filter === "needs" ? rows.filter((x) => x.gaps.length) : rows;
  // Every part here is on one of the project's builds (a part is in the projects whose BOMs use it), so it has a supply.
  const cmName = (() => {
    const v = project.cm_vendor_id ? px.vendorById.get(project.cm_vendor_id) : undefined;
    return v?.short_name || v?.name || "";
  })();
  // In the same words as a run's Who supplies each part.
  const supplyText = (p: ScopePart) => (!p.uses.length ? "—" : p.supplies.size > 1 ? "Mixed" : supplyWords([...p.supplies][0], cmName));
  // Runs of this project that say otherwise for a part (Plan → Who supplies), by part.
  const runSupply = useRows("production_run_supply");
  const allRuns = useRows("production_run");
  const runsSay = useMemo(() => {
    const runs = new Map(allRuns.filter((r) => r.project_id === project.id).map((r) => [r.id, r]));
    const by = new Map<string, { number: string; text: string }[]>();
    for (const s of runSupply) {
      const run = runs.get(s.run_id);
      if (!run) continue;
      const cm = run.cm_vendor_id ? px.vendorById.get(run.cm_vendor_id) : undefined;
      by.set(s.item_id, [...(by.get(s.item_id) ?? []), { number: run.number, text: supplyWords(s.supply, cm?.short_name || cm?.name || "") }]);
    }
    return by;
  }, [runSupply, allRuns, project.id, px]);
  const dash = <span className="text-faint">—</span>;
  // What a part goes into: the builds whose lines use it, by name
  const usedIn = (p: ScopePart) => [...new Set(p.uses.map((u) => scope.families.find((f) => f.key === u.family)?.label ?? ""))].filter(Boolean);
  const columns: Column<SourcingRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,150px)",
      value: (x) => x.p.item.sku,
      cell: (x) => <span className="truncate font-mono text-[12px]">{x.p.item.sku}</span>,
    },
    { key: "name", header: "Name", width: "minmax(180px,1fr)", value: (x) => x.p.item.name },
    {
      key: "used_in",
      header: "Goes into",
      headerTitle: "The project's builds that use it",
      width: "minmax(150px,0.8fr)",
      value: (x) => usedIn(x.p).join(", "),
      cell: (x) => (
        <span className="truncate text-[12px] text-muted" title={usedIn(x.p).join(", ")}>
          {usedIn(x.p).join(", ")}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: "110px",
      optional: true,
      value: (x) => typeLabel(x.p.item),
      cell: (x) => <span className="text-[12px] text-faint">{typeOf(x.p.item) !== "material" ? typeLabel(x.p.item) : ""}</span>,
    },
    {
      key: "supply",
      header: "Who supplies it",
      headerTitle: "We send it to the maker, the maker buys it, or the maker makes it (its BOM: a change goes on every build here that uses it)",
      width: "minmax(160px,200px)",
      value: (x) => supplyText(x.p),
      cell: (x) => <span className="truncate text-[12px]">{supplyText(x.p)}</span>,
      edit: {
        options: RUN_SUPPLIES.map((v) => ({ value: v, label: supplyWords(v, cmName) })),
        get: (x) => (x.p.supplies.size === 1 ? [...x.p.supplies][0] : ""),
        save: (x, supply) => {
          // Each of the project's BOM lines that has it (bom.set_line_supply: that family's revision in use and any draft)
          for (const u of x.p.uses) if (u.supply !== supply) mutate("bom.set_line_supply", { line_id: u.lineId, supply: supply as Supply });
        },
        can: (x) => x.p.uses.length > 0,
      },
    },
    {
      key: "vendor",
      header: "Vendor",
      width: "minmax(120px,0.6fr)",
      value: (x) => (x.s.vendor ? x.s.vendor.short_name || x.s.vendor.name : x.cmOnly ? (cmName ? `${cmName}'s` : "The CM's") : "No vendor"),
      cell: ({ s, cmOnly }) =>
        s.vendor ? (
          <PaneLink kind="vendor" id={s.vendor.id} to={`/vendors?open=${s.vendor.id}`} className="truncate hover:underline">
            {s.vendor.short_name || s.vendor.name}
          </PaneLink>
        ) : cmOnly ? (
          <span className="truncate text-muted" title={`${cmName || "The CM"} buys or makes it, so HHL doesn't need a vendor for it`}>
            {cmName ? `${cmName}'s` : "The CM's"}
          </span>
        ) : (
          <Missing>No vendor</Missing>
        ),
    },
    {
      key: "runs",
      header: "Runs say otherwise",
      headerTitle: "A run of this project that says otherwise for its batch: change it on its Plan tab",
      width: "minmax(130px,0.6fr)",
      value: (x) => (runsSay.get(x.p.item.id) ?? []).map((d) => `${d.number}: ${d.text}`).join(", "),
      cell: (x) => (
        <span className="flex min-w-0 gap-2 truncate">
          {(runsSay.get(x.p.item.id) ?? []).map((d) => (
            <Link
              key={d.number}
              to={runPath(d)}
              onClick={(e) => e.stopPropagation()}
              className="truncate text-[11px] text-accent-text hover:underline"
              title={`${d.number} says otherwise for its batch: change it on its Plan tab`}
            >
              {d.number}: {d.text}
            </Link>
          ))}
        </span>
      ),
    },
    {
      key: "price",
      header: "Price",
      width: "90px",
      align: "right",
      value: (x) => x.s.price,
      cell: ({ s, cmOnly }) => (
        <span className="font-mono text-[12px]">{!s.link ? "" : s.price == null ? cmOnly ? dash : <Missing>No price</Missing> : money(s.price)}</span>
      ),
    },
    {
      key: "moq",
      header: "MOQ",
      width: "90px",
      align: "right",
      value: (x) => (x.s.link && x.terms ? x.s.moq : null),
      text: (x) => (x.s.link && x.terms && x.s.moq != null ? `${x.s.moq}${x.s.moqFrom === "vendor" ? "*" : ""}` : ""),
      cell: ({ s, terms }) => (
        <span className="font-mono text-[12px]" title={s.moqFrom === "vendor" ? "The vendor's default" : undefined}>
          {!s.link || !terms ? dash : s.moq == null ? <Missing>No MOQ</Missing> : `${qty(s.moq)}${s.moqFrom === "vendor" ? "*" : ""}`}
        </span>
      ),
    },
    {
      key: "lead",
      header: "Lead time",
      width: "100px",
      align: "right",
      value: (x) => (x.s.link && x.terms ? x.s.lead : null),
      text: (x) => (x.s.link && x.terms && x.s.lead != null ? `${days(x.s.lead)}${x.s.leadFrom === "vendor" ? "*" : ""}` : ""),
      cell: ({ s, terms }) => (
        <span className="font-mono text-[12px]" title={s.leadFrom === "vendor" ? "The vendor's default" : undefined}>
          {!s.link || !terms ? dash : s.lead == null ? <Missing>No lead time</Missing> : `${days(s.lead)}${s.leadFrom === "vendor" ? "*" : ""}`}
        </span>
      ),
    },
    // Needs work first (as the list always was): what each still needs, for the Needs work rows
    {
      key: "needs",
      header: "Needs",
      width: "140px",
      optional: true,
      value: (x) => (x.gaps.length ? 1 : 0),
      text: (x) => x.gaps.join(", "),
      cell: (x) => <span className="truncate text-[12px] text-muted">{x.gaps.join(", ")}</span>,
    },
  ];
  return (
    <div className="flex flex-col gap-3 p-4">
      <StepGaps r={r} steps={["sourcing"]} project={project} px={px} here={here} />
      <BuildSupply {...{ project, px, scope, r, here }} />
      <PageSection
        title="Parts and who supplies them"
        note="Every part the builds use, what it goes into and who gets it to the maker; for what we buy, from whom and on what terms"
        actions={<Filter value={filter} onChange={setFilter} counts={[rows.filter((x) => x.gaps.length).length, rows.length]} />}
        foot={
          <>
            <div className="text-[12px] text-muted">
              Who supplies it is from the BOMs, for every run from now on. For one batch only, change it on that run's Units tab (
              <Link to={`/runs?project=${encodeURIComponent(project.code || project.id)}`} className="text-accent-text hover:underline">
                Runs
              </Link>
              ), under Who supplies each part.
            </div>
            <div className="mt-1">* the vendor's default, from the vendor's panel</div>
          </>
        }
      >
        {(slot) => (
          <DataTable
            fit={20}
            rows={shown}
            columns={columns}
            defaultSort={{ key: "needs", desc: true }}
            editing={can("bom.set_line_supply")}
            onOpen={(x) => open(x.p.item.id)}
            exportName="Parts and who supplies them"
            bar={slot}
            pageFilters={false}
            rowNoun={["part", "parts"]}
            empty={
              <div className="px-4 py-3 text-muted">
                {rows.length ? "Every part has what it needs." : scope.parts.size ? "No parts yet." : <NoParts project={project} scope={scope} />}
              </div>
            }
          />
        )}
      </PageSection>
    </div>
  );
}

// Files ------------------------------------------------------------------------------------------------

/**
 * Packaging --- whether a part's files need work: a packaging part says its Category; a part that needs a spec has a
 * released one with every required file in it. Plain and labelled packaging need nothing.
 */
function needsWork(f: ReturnType<typeof filesOf>) {
  if (f.pack && !f.pack.type) return true;
  if (!f.needs.spec) return f.missing.required.length > 0;
  if (!f.spec || !f.rev) return true;
  return f.needs.files.some((n) => !n.optional && !n.kinds.some((k) => f.released?.has(k)));
}

type FilesRow = { id: string; p: ScopePart; f: ReturnType<typeof filesOf>; needs: boolean };

export function FilesTab({ project, px, scope, r, here }: TabProps) {
  const [filter, setFilter] = useState<"needs" | "all">("needs");
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const editable = can("item.set_part_type");
  const rows = useMemo<FilesRow[]>(
    () =>
      partList(scope)
        .filter((p) => p.item.kind !== "service")
        .map((p) => {
          const f = filesOf(px, p.item);
          return { id: p.item.id, p, f, needs: needsWork(f) };
        })
        .sort((a, b) => Number(b.needs) - Number(a.needs)),
    [px, scope],
  );
  const shown = filter === "needs" ? rows.filter((x) => x.needs) : rows;
  // The materials' Categories, for every material and sub-assembly ticked (packaging's and services' aren't these)
  const typeOptions = categoryOptions("material");
  const bulk = (item: ProjectItem) => typeOptions.length > 0 && ["material", "subassembly"].includes(typeOf(asPart(item)));
  // Only materials and sub-assemblies tick: a packaging part's Category is set on its own row
  const tickable = new Set(rows.filter((x) => bulk(x.p.item)).map((x) => x.id));
  const optionsOf = (item: ProjectItem) => {
    const part = asPart(item);
    const type = typeOf(part);
    const options = categoryOptions(type);
    return type === "packaging" && !categoryOf(part) ? [{ value: "", label: "Not set" }, ...options] : options;
  };
  const columns: Column<FilesRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,150px)",
      value: (x) => x.p.item.sku,
      cell: (x) => <span className="truncate font-mono text-[12px]">{x.p.item.sku}</span>,
    },
    { key: "name", header: "Name", width: "minmax(180px,1fr)", value: (x) => x.p.item.name },
    {
      // Packaging --- what it is: "Printed box", "Plain bag"
      key: "packaging",
      header: "Packaging",
      width: "minmax(100px,140px)",
      value: (x) => (x.f.pack?.type ? x.f.pack.title : ""),
      cell: (x) =>
        x.f.pack?.type ? (
          <span className="truncate text-[12px] text-faint" title={x.f.pack.says}>
            {x.f.pack.title}
          </span>
        ) : null,
    },
    {
      key: "category",
      header: "Category",
      headerTitle: "A material's or sub-assembly's Category decides its spec's files; a packaging part's is its kind",
      width: "130px",
      value: (x) => categoryLabel(asPart(x.p.item)) || (typeOf(asPart(x.p.item)) === "packaging" ? "Not set" : ""),
      cell: (x) => {
        const part = asPart(x.p.item);
        if (!categoryOptions(typeOf(part)).length) return <span className="text-[12px] text-faint">—</span>;
        return <span className="truncate text-[12px]">{categoryLabel(part) || <span className="text-faint">Not set</span>}</span>;
      },
      edit: {
        options: (x) => optionsOf(x.p.item),
        get: (x) => categoryOf(asPart(x.p.item)),
        save: (x, value) => {
          const item = x.p.item;
          if (!value || value === categoryOf(asPart(item))) return;
          if (typeOf(asPart(item)) === "packaging") mutate("item.update", { id: item.id, patch: packCategoryPatch(item, value as PackType) });
          else mutate("item.set_part_type", { item_ids: [item.id], part_type: value as PartType });
        },
        can: (x) => categoryOptions(typeOf(asPart(x.p.item))).length > 0,
      },
    },
    {
      key: "spec",
      header: "Spec",
      width: "150px",
      value: (x) => (!x.f.spec ? (x.f.needs.spec ? "No spec" : "Not needed") : x.f.rev ? `Rev ${x.f.rev}` : "Draft, not released"),
      cell: ({ p, f }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation(); // its spec, not the row's part
            setSearch({ spec: p.item.id });
          }}
          className="flex items-center text-left"
        >
          {!f.spec ? (
            f.needs.spec ? (
              <Badge tone="neutral">No spec</Badge>
            ) : (
              <span className="text-[11px] text-faint" title="Nothing to tell the vendor but the part">
                Not needed
              </span>
            )
          ) : f.rev ? (
            <Badge tone="ok">Rev {f.rev}</Badge>
          ) : (
            <Badge tone="accent">Draft, not released</Badge>
          )}
        </button>
      ),
    },
    {
      key: "files",
      header: "Files it needs",
      width: "minmax(200px,1.4fr)",
      value: (x) => (x.needs ? "Needs work" : "Done"),
      cell: ({ p, f }) => <NeedChips item={p.item} f={f} onKind={() => open(p.item.id)} className="min-w-0" />,
    },
    // Needs work first (as the list always was)
    {
      key: "needs",
      header: "Needs work",
      width: "100px",
      optional: true,
      value: (x) => (x.needs ? 1 : 0),
      text: (x) => (x.needs ? "yes" : ""),
      cell: (x) => (x.needs ? <span className="text-[12px] text-warn">Needs work</span> : null),
    },
  ];
  return (
    <div className="flex flex-col gap-3 p-4">
      <StepGaps r={r} steps={["files"]} project={project} px={px} here={here} />
      <PageSection
        title="Production files"
        note="Each part's spec and the files it needs: ✓ in the released revision (what vendors get with each PO), “not released” only in the draft"
        actions={
          <>
            {/* The ticked materials and sub-assemblies' Category, all at once */}
            {editable && picked.size > 0 && (
              <span className="flex items-center gap-2 rounded-md bg-accent-soft/50 py-0.5 pr-0.5 pl-2 text-[12px]">
                <span className="font-medium">{picked.size} ticked</span>
                <span className="text-muted">Set their Category</span>
                <span className="w-36">
                  <Select
                    value=""
                    options={[{ value: "", label: "Choose…" }, ...typeOptions]}
                    onChange={async (e) => {
                      if (!e.target.value) return;
                      if (await mutate("item.set_part_type", { item_ids: [...picked], part_type: e.target.value as PartType })) setPicked(new Set());
                    }}
                    className="h-7"
                  />
                </span>
                <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
                  Clear
                </Button>
              </span>
            )}
            <Filter value={filter} onChange={setFilter} counts={[rows.filter((x) => x.needs).length, rows.length]} />
          </>
        }
      >
        {(slot) => (
          <DataTable
            fit={20}
            rows={shown}
            columns={columns}
            defaultSort={{ key: "needs", desc: true }}
            editing={editable}
            selection={editable ? { selected: picked, onChange: setPicked } : undefined}
            selectable={(x) => tickable.has(x.id)}
            onOpen={(x) => open(x.p.item.id)}
            exportName="Production files"
            bar={slot}
            pageFilters={false}
            rowNoun={["part", "parts"]}
            empty={
              <div className="px-4 py-3 text-muted">
                {rows.length ? "Every part has a released spec with its files." : <NoParts project={project} scope={scope} />}
              </div>
            }
          />
        )}
      </PageSection>
    </div>
  );
}
